import React from "react";
import { NavLink, Link, useLocation, Navigate } from "react-router-dom";
import { Home, Search, PlusSquare, Heart, Send, Radio, User as UserIcon, LogIn, Tv, Menu } from "lucide-react";
import { useAuth } from "../context/AuthContext";

/**
 * SocialShell — Tap IN Social with a TikTok / Instagram look.
 *
 *  • Logged-out users see a minimal splash that uses the existing Tap IN
 *    auth (no separate accounts).
 *  • Mobile: minimal top app bar (logo + bell + DM), edge-to-edge content,
 *    bottom tab bar with 5 icon-only tabs.
 *  • Desktop: left sidebar like Instagram desktop, center column ~470px
 *    wide for the feed, no bottom bar, no banner.
 */

// ── desktop sidebar config ─────────────────────────────────────────
const SIDEBAR = [
  { to: "/social",               icon: Home,        label: "Home",          end: true,  testid: "snav-feed" },
  { to: "/social/explore",       icon: Search,      label: "Search",                  testid: "snav-explore" },
  { to: "/social/live",          icon: Radio,       label: "Live",                    testid: "snav-live" },
  { to: "/social/notifications", icon: Heart,       label: "Notifications",           testid: "snav-notifications" },
  { to: "/social/messages",      icon: Send,        label: "Messages",                testid: "snav-messages" },
  { to: "/social/create",        icon: PlusSquare,  label: "Create",                  testid: "snav-create" },
  { to: "/social/me",            icon: UserIcon,    label: "Profile",                 testid: "snav-me" },
];

// ── mobile bottom-bar config (5 slots, icon-only) ──────────────────
const BOTTOM_TABS = [
  { to: "/social",               icon: Home,       end: true, testid: "snav-feed" },
  { to: "/social/explore",       icon: Search,                testid: "snav-explore" },
  { to: "/social/create",        icon: PlusSquare,            testid: "snav-create" },
  { to: "/social/live",          icon: Radio,                 testid: "snav-live" },
  { to: "/social/me",            icon: UserIcon,              testid: "snav-me" },
];

export default function SocialShell({ children }) {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  const isWelcome = pathname === "/social/welcome";

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-white/60">Loading…</div>;
  }
  if (!user && !isWelcome) {
    return <Navigate to="/social/welcome" replace state={{ from: pathname }} />;
  }
  if (!user && isWelcome) {
    return <SocialSplash />;
  }

  return (
    <div data-testid="social-shell" className="min-h-screen bg-black text-white flex">
      {/* Desktop sidebar (Instagram-style) */}
      <aside
        data-testid="social-sidebar"
        className="hidden lg:flex flex-col fixed left-0 top-0 bottom-0 w-[244px] border-r border-white/10 px-4 py-6 z-40"
      >
        <Link to="/social" className="block px-2 py-2 mb-6" data-testid="social-brand">
          <img src="/tapin-social-logo.png" alt="Tap IN Social" className="h-10 w-auto drop-shadow-[0_0_12px_rgba(239,68,68,0.6)]" />
        </Link>
        <nav className="space-y-1">
          {SIDEBAR.map((t) => {
            const Icon = t.icon;
            return (
              <NavLink
                key={t.to}
                to={t.to}
                end={!!t.end}
                data-testid={t.testid}
                className={({ isActive }) =>
                  `flex items-center gap-4 px-3 py-3 rounded-lg text-base font-medium transition ${isActive ? "bg-white/10 text-white font-bold" : "text-white/85 hover:bg-white/5"}`
                }
              >
                <Icon className="w-6 h-6" /> {t.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="mt-auto pt-6 border-t border-white/10">
          <Link
            to="/"
            data-testid="back-to-tapintv-desktop"
            className="flex items-center gap-3 px-3 py-2 text-white/60 hover:text-white text-sm"
          ><Tv className="w-4 h-4" /> Tap IN TV</Link>
        </div>
      </aside>

      {/* Center content column */}
      <div className="flex-1 lg:ml-[244px] min-w-0">
        {/* Mobile top app bar — minimal, app-style */}
        <header
          data-testid="social-header-mobile"
          className="lg:hidden sticky top-0 z-50 bg-black/95 backdrop-blur-xl border-b border-white/10 flex items-center justify-between px-4 py-2.5"
        >
          <Link to="/social" data-testid="social-brand-mobile">
            <img src="/tapin-social-logo.png" alt="Tap IN Social" className="h-7 w-auto drop-shadow-[0_0_8px_rgba(239,68,68,0.5)]" />
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/social/notifications" data-testid="topbar-notifications" className="p-2 -mr-1 text-white hover:text-red-400" aria-label="Notifications">
              <Heart className="w-6 h-6" />
            </Link>
            <Link to="/social/messages" data-testid="topbar-messages" className="p-2 text-white hover:text-red-400" aria-label="Messages">
              <Send className="w-6 h-6" />
            </Link>
            <Link to="/" data-testid="back-to-tapintv-mobile" className="p-2 text-white/60 hover:text-white" aria-label="Back to Tap IN TV">
              <Tv className="w-5 h-5" />
            </Link>
          </div>
        </header>

        {/* Edge-to-edge on mobile, centered narrow column on desktop */}
        <main className="lg:max-w-[470px] lg:mx-auto pb-20 lg:pb-8">
          {children}
        </main>
      </div>

      {/* Mobile bottom tab bar — 5 icons, no labels */}
      <nav
        data-testid="social-tabbar"
        className="lg:hidden fixed bottom-0 inset-x-0 z-50 bg-black/95 backdrop-blur-xl border-t border-white/10 flex items-center justify-around px-2 pt-2 pb-[max(8px,env(safe-area-inset-bottom))]"
      >
        {BOTTOM_TABS.map((t) => {
          const Icon = t.icon;
          return (
            <NavLink
              key={t.to}
              to={t.to}
              end={!!t.end}
              data-testid={t.testid}
              className={({ isActive }) =>
                `p-2 rounded-md ${isActive ? "text-white" : "text-white/60 hover:text-white"}`
              }
            >
              <Icon className="w-7 h-7" strokeWidth={isActiveStroke(t.testid, pathname)} />
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}

// Slightly thicker stroke when the tab is active (Instagram-style)
function isActiveStroke(testid, pathname) {
  const map = {
    "snav-feed":     pathname === "/social",
    "snav-explore":  pathname.startsWith("/social/explore"),
    "snav-create":   pathname.startsWith("/social/create"),
    "snav-live":     pathname.startsWith("/social/live"),
    "snav-me":       pathname.startsWith("/social/me"),
  };
  return map[testid] ? 2.5 : 1.6;
}

/* ─────────────────────────────────────────────────────────── */
/* Splash for logged-out users                                 */
/* ─────────────────────────────────────────────────────────── */
function SocialSplash() {
  return (
    <div data-testid="social-splash" className="min-h-screen bg-gradient-to-br from-[#0a0608] via-[#1a0608] to-[#080204] text-white flex flex-col items-center justify-center px-6 text-center relative overflow-hidden">
      <div aria-hidden className="absolute inset-0 opacity-40" style={{
        backgroundImage: "radial-gradient(circle at 30% 30%, rgba(239,68,68,0.4), transparent 50%), radial-gradient(circle at 70% 80%, rgba(190,18,60,0.4), transparent 55%)",
      }} />
      <div className="relative z-10 max-w-md w-full">
        <img src="/tapin-social-logo.png" alt="Tap IN Social" className="h-20 sm:h-28 mx-auto drop-shadow-[0_0_40px_rgba(239,68,68,0.6)]" />
        <h1 data-testid="splash-heading" className="font-display font-black text-3xl sm:text-5xl tracking-tighter mt-6 leading-[1.05]">
          BE YOU.<br />BE HEARD.<br /><span className="text-red-500">BELONG.</span>
        </h1>
        <p className="text-white/70 mt-4 text-sm sm:text-base">
          The social network that keeps it real. Sign in with your Tap IN TV account — same login, same you.
        </p>
        <div className="mt-8 flex flex-col gap-3">
          <Link
            to="/login?next=/social"
            data-testid="splash-login"
            className="bg-red-500 hover:bg-red-600 text-white font-black py-3.5 rounded-full inline-flex items-center justify-center gap-2 shadow-2xl"
          ><LogIn className="w-4 h-4" /> Sign in to Tap IN Social</Link>
          <Link
            to="/signup?next=/social"
            data-testid="splash-signup"
            className="bg-white/10 hover:bg-white/20 text-white font-bold py-3 rounded-full"
          >Create a free Tap IN account</Link>
          <Link to="/" className="text-white/50 hover:text-white/80 text-xs mt-2 inline-flex items-center gap-1 justify-center">
            <Menu className="w-3 h-3" /> Back to Tap IN TV
          </Link>
        </div>
      </div>
    </div>
  );
}
